import React, { createContext, useState, useContext, useEffect } from 'react';

// Currency context for managing currency preferences
const CurrencyContext = createContext();

// Exchange rate (can be updated from API or config)
const USD_TO_INR_RATE = 83; // Approximate exchange rate

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('INR'); // Default to INR
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Detect user's location using various methods
    const detectCurrency = async () => {
      try {
        // Method 1: Check localStorage for saved preference
        const savedCurrency = localStorage.getItem('preferredCurrency');
        if (savedCurrency) {
          setCurrency(savedCurrency);
          setIsLoading(false);
          return;
        }
        
        // Method 2: Use browser's timezone to guess location
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const isIndianTimezone = timezone && timezone.includes('Kolkata');
        
        // Method 3: Use browser language
        const browserLang = navigator.language || navigator.userLanguage;
        const isIndianLang = browserLang && (browserLang.includes('hi') || browserLang.includes('en-IN'));
        
        // Method 4: IP-based geolocation (fallback)
        // Note: In production, you'd use a proper geolocation API
        try {
          const response = await fetch('https://ipapi.co/json/');
          const data = await response.json();
          if (data.country_code === 'IN') {
            setCurrency('INR');
          } else {
            setCurrency('USD');
          }
        } catch (geoError) {
          // If geolocation fails, use timezone/language detection
          setCurrency(isIndianTimezone || isIndianLang ? 'INR' : 'USD');
        }
      } catch (error) {
        console.error('Error detecting currency:', error);
        setCurrency('INR'); // Default to INR
      } finally {
        setIsLoading(false);
      }
    };
    
    detectCurrency();
  }, []);
  
  // Convert price from INR to selected currency
  const convertPrice = (priceInINR) => {
    if (!priceInINR || isNaN(priceInINR)) return null;
    
    if (currency === 'INR') {
      return Math.round(priceInINR);
    } else {
      // Convert to USD
      return Math.round(priceInINR / USD_TO_INR_RATE);
    }
  };
  
  // Format price with currency symbol
  const formatPrice = (priceInINR, options = {}) => {
    if (!priceInINR || isNaN(priceInINR)) {
      return options.fallback || 'Contact for pricing';
    }
    
    const convertedPrice = convertPrice(priceInINR);
    
    if (currency === 'INR') {
      return `₹${convertedPrice.toLocaleString('en-IN')}`;
    } else {
      return `$${convertedPrice.toLocaleString('en-US')}`;
    }
  };
  
  // Format price range
  const formatPriceRange = (minPrice, maxPrice) => {
    if (!minPrice || isNaN(minPrice)) return null;
    
    if (maxPrice && !isNaN(maxPrice)) {
      return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
    } else {
      return `${formatPrice(minPrice)}+`;
    }
  };
  
  // Update currency preference
  const updateCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem('preferredCurrency', newCurrency);
  };
  
  // Get currency symbol
  const getCurrencySymbol = () => {
    return currency === 'INR' ? '₹' : '$';
  };
  
  const value = {
    currency,
    isLoading,
    convertPrice,
    formatPrice,
    formatPriceRange,
    updateCurrency,
    getCurrencySymbol,
    exchangeRate: USD_TO_INR_RATE
  };
  
  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Custom hook to use currency context
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};